// errorElement for the public /i/:slug route. A client must NEVER see React
// Router's raw "Unexpected Application Error!" dev screen (it leaks technical
// messages). This is a self-contained, always-light branded screen: it wraps
// its own inquiry ThemeProvider because the form's nested theme provider lives
// inside InquiryFormScreen, which failed to render. The underlying error is
// logged for the baker, not shown.

import { ThemeProvider, Avatar, Box, Button, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useRouteError } from "react-router-dom";
import { buildInquiryTheme } from "@/features/inquiry-form/theme";

export function InquiryFormErrorScreen(): React.ReactElement {
  const error = useRouteError();
  // Surface the real cause in the console for debugging; never to the client.
  console.error("Inquiry form route error", error);

  const theme = buildInquiryTheme();

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          "@supports (height: 100dvh)": { minHeight: "100dvh" },
          bgcolor: "background.default",
          color: "text.primary",
          fontFamily: (t) => t.typography.fontFamily,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Stack spacing={3} sx={{ alignItems: "center", textAlign: "center", maxWidth: 360 }}>
          <Box
            sx={{
              width: 84,
              height: 84,
              borderRadius: "50%",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Avatar sx={{ width: 60, height: 60, bgcolor: "primary.main" }}>
              <ErrorOutlineIcon />
            </Avatar>
          </Box>
          <Typography variant="h2">Something went wrong</Typography>
          <Typography variant="body1" color="text.secondary">
            We couldn&apos;t load this form just now. Please refresh the page — if it
            keeps happening, reach out to the bakery directly and we&apos;ll help you
            place your order.
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              window.location.reload();
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>
    </ThemeProvider>
  );
}
